import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ITab } from '@tecalisdev/infinity';
import { LanguageService } from '../../core/services/language.service';
import { OPERATION_TYPE } from '../../operations/models/operations.models';
import { WorkflowStep } from '../models/workflows.models';
import {
	inferLocaleScrollAnchorFromPath,
	localeTextControlPathForOperation,
	operationTypeToLocaleScrollAnchor as mapOperationTypeToLocaleScrollAnchor,
	withSelectedWorkflowLocaleTab,
	WORKFLOW_LOCALE_SCROLL_ANCHOR_IDS,
	WorkflowLocaleScrollAnchor,
} from '../utils/workflow-locale-scroll.util';
import { firstMissingLocaleTextId, resolveWorkflowLocaleText } from '../utils/workflow-locale-text.util';
import { WorkflowFormValidationService } from './workflow-form-validation.service';

/**
 * Host multi-idioma / scroll a locale inválido (NIF-845).
 */
export interface WorkflowFormLocalesHost {
	form: FormGroup;
	workflowLocaleTabs: ITab[];
	selectedWorkflowLocaleId: string;
	expandHomeIndicationsForLocaleScroll: boolean;
	expandDocumentsAdvancedForLocaleScroll: boolean;
	readonly formStepsArray: { at(index: number): AbstractControl };
	cdr: { detectChanges(): void };
}

/**
 * Tabs de idioma y focus/scroll a textos multi-locale (NIF-845).
 *
 * Abrir cuando: `buildWorkflowLocaleTabs`, `focusWorkflowLocaleOnInvalidStep`.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormLocalesService {
	private readonly languageService = inject(LanguageService);
	private readonly trans = inject(TranslateService);
	private readonly validationService = inject(WorkflowFormValidationService);

	public buildWorkflowLocaleTabs(host: WorkflowFormLocalesHost): void {
		const langs = this.languageService.getAllowedLanguages();
		host.selectedWorkflowLocaleId = langs[0] ?? 'es';
		host.workflowLocaleTabs = langs.map((lang) => ({
			id: lang,
			label: this.trans.instant(`MASTER.LANGUAGE.${lang.toUpperCase()}`),
			selected: lang === host.selectedWorkflowLocaleId,
		}));
	}

	public onWorkflowLocaleTabSelected(host: WorkflowFormLocalesHost, tab: { id: string; label: string }): void {
		const id = tab?.id != null ? String(tab.id) : '';
		if (!id) {
			return;
		}
		host.selectedWorkflowLocaleId = id;
		host.workflowLocaleTabs = withSelectedWorkflowLocaleTab(host.workflowLocaleTabs, id);
	}

	public getWorkflowLocaleControl(host: WorkflowFormLocalesHost, ...segments: string[]): FormControl {
		let c: AbstractControl | null = host.form;
		for (const s of segments) {
			c = c?.get(s) ?? null;
		}
		return c as FormControl;
	}

	public resolveDocRowTitle(value: unknown): string {
		return resolveWorkflowLocaleText(
			value as Parameters<typeof resolveWorkflowLocaleText>[0],
			this.languageService.currentLang(),
		);
	}

	public resolveFormStepListTitle(host: WorkflowFormLocalesHost, stepIndex: number): string {
		const value = host.formStepsArray.at(stepIndex).get('step_name')?.value;
		return resolveWorkflowLocaleText(
			value as Parameters<typeof resolveWorkflowLocaleText>[0],
			this.languageService.currentLang(),
		);
	}

	public selectWorkflowLocaleTab(host: WorkflowFormLocalesHost, localeId: string): void {
		const id = String(localeId);
		if (!id) {
			return;
		}
		host.selectedWorkflowLocaleId = id;
		host.workflowLocaleTabs = withSelectedWorkflowLocaleTab(host.workflowLocaleTabs, id);
	}

	public getLocaleGroupForOperation(host: WorkflowFormLocalesHost, operationType: string): FormGroup | null {
		const relative = localeTextControlPathForOperation(operationType);
		if (!relative) {
			return null;
		}
		return host.form.get(`operations.${operationType}.${relative}`) as FormGroup;
	}

	public scheduleScrollToWorkflowLocaleAnchor(
		host: WorkflowFormLocalesHost,
		anchor: WorkflowLocaleScrollAnchor,
	): void {
		if (anchor === 'home') {
			host.expandHomeIndicationsForLocaleScroll = true;
		}
		if (anchor === 'documents') {
			host.expandDocumentsAdvancedForLocaleScroll = true;
		}
		host.cdr.detectChanges();
		setTimeout(() => {
			const el = document.getElementById(WORKFLOW_LOCALE_SCROLL_ANCHOR_IDS[anchor]);
			el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
			const focusable = el?.querySelector('textarea, input') as HTMLElement | null;
			focusable?.focus?.();
		}, 320);
	}

	public focusWorkflowLocaleForOperation(host: WorkflowFormLocalesHost, operationType: string): boolean {
		const allowed = this.languageService.getAllowedLanguages();
		const opGroup = host.form.get(`operations.${operationType}`) as FormGroup;
		if (!opGroup) {
			return false;
		}
		const path = this.validationService.findFirstInvalidControlPath(opGroup);
		if (path?.length && allowed.includes(path[path.length - 1])) {
			this.selectWorkflowLocaleTab(host, path[path.length - 1]);
			const ctx = inferLocaleScrollAnchorFromPath(WorkflowStep.OPERATIONS, path);
			if (ctx) {
				this.scheduleScrollToWorkflowLocaleAnchor(host, ctx);
				return true;
			}
			return false;
		}
		const g = this.getLocaleGroupForOperation(host, operationType);
		if (g?.invalid) {
			const id = firstMissingLocaleTextId(g, allowed);
			if (id) {
				this.selectWorkflowLocaleTab(host, id);
				const anchor = mapOperationTypeToLocaleScrollAnchor(operationType);
				if (anchor) {
					this.scheduleScrollToWorkflowLocaleAnchor(host, anchor);
					return true;
				}
			}
		}
		return false;
	}

	public focusWorkflowLocaleOnInvalidStep(host: WorkflowFormLocalesHost, stepId: string): boolean {
		const allowed = this.languageService.getAllowedLanguages();
		const stepControl = host.form.get(stepId);
		if (!stepControl) {
			return false;
		}

		const path = this.validationService.findFirstInvalidControlPath(stepControl);
		if (path?.length && allowed.includes(path[path.length - 1])) {
			this.selectWorkflowLocaleTab(host, path[path.length - 1]);
			const scrollCtx = inferLocaleScrollAnchorFromPath(stepId, path);
			if (scrollCtx) {
				this.scheduleScrollToWorkflowLocaleAnchor(host, scrollCtx);
				return true;
			}
			return false;
		}

		if (stepId === WorkflowStep.GENERAL) {
			const delayed = host.form.get('general.delayed_process_warning_text') as FormGroup;
			if (delayed?.invalid) {
				const id = firstMissingLocaleTextId(delayed, allowed);
				if (id) {
					this.selectWorkflowLocaleTab(host, id);
					this.scheduleScrollToWorkflowLocaleAnchor(host, 'general-delayed');
					return true;
				}
			}
			return false;
		}

		if (stepId === WorkflowStep.OPERATIONS) {
			const localeGroups: Array<{ group: AbstractControl | null; context: WorkflowLocaleScrollAnchor }> = [
				{ group: host.form.get(`operations.${OPERATION_TYPE.HOME}.description`), context: 'home' },
				{ group: host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.title`), context: 'documents' },
			];
			for (const { group, context } of localeGroups) {
				if (group?.invalid) {
					const id = firstMissingLocaleTextId(group as FormGroup, allowed);
					if (id) {
						this.selectWorkflowLocaleTab(host, id);
						this.scheduleScrollToWorkflowLocaleAnchor(host, context);
						return true;
					}
				}
			}
		}
		return false;
	}
}
